import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { Subscription } from 'rxjs';

import { toDisplayDate, toLocalDate, toTypingDate } from '../utils/date-utils';

@Directive({
  selector: 'input[bsDatepicker]',
})
export class DatepickerDisplayDirective implements OnInit, OnDestroy {
  private focused = false;
  private readonly subs = new Subscription();

  constructor(
    private readonly elRef: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
    @Optional() @Self() private readonly picker: BsDatepickerDirective | null,
    @Optional() @Self() private readonly ngControl: NgControl | null,
  ) {}

  ngOnInit(): void {
    // Fired when a day is picked in the calendar, and when a value is written into the
    // control programmatically (draft prefill, spouse auto-populate, min/max correction).
    if (this.picker?.bsValueChange) {
      this.subs.add(this.picker.bsValueChange.subscribe(() => this.renderLater()));
    }

    // Covers patchValue/setValue calls that do not go through the picker.
    if (this.ngControl?.valueChanges) {
      this.subs.add(this.ngControl.valueChanges.subscribe(() => this.renderLater()));
    }

    this.renderLater();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('mousedown')
  onMouseDown(): void {
    this.showTypingFormat();
  }

  @HostListener('focus')
  onFocus(): void {
    this.focused = true;
    // Covers keyboard (Tab) focus. After a mousedown this is already a no-op.
    this.showTypingFormat();
  }

  @HostListener('keydown')
  onKeyDown(): void {
    this.showTypingFormat();
  }

  @HostListener('blur')
  onBlur(): void {
    this.focused = false;
    this.render();
  }

  private renderLater(): void {
    setTimeout(() => {
      if (!this.focused) {
        this.render();
      }
    }, 0);
  }

  private showTypingFormat(): void {
    const el = this.elRef.nativeElement;
    const date = this.currentDate();
    if (!date) {
      return;
    }
    if (el.value !== toDisplayDate(date)) {
      // Already digits (or something the user is typing) - leave it alone.
      return;
    }
    this.renderer.setProperty(el, 'value', toTypingDate(date));
  }

  /** Puts the field into the MMM-DD-YYYY form the user reads. */
  private render(): void {
    const el = this.elRef.nativeElement;
    const date = this.currentDate();
    if (!date) {
      // No usable value: leave whatever the user has typed alone. Clearing it here would
      // wipe half-finished input.
      return;
    }
    const text = toDisplayDate(date);
    if (el.value !== text) {
      this.renderer.setProperty(el, 'value', text);
    }
  }

  private currentDate(): Date | null {
    return toLocalDate(this.ngControl ? this.ngControl.value : this.picker?.bsValue);
  }
}
